"use client";

import { useCallback, useMemo, useState } from "react";
import { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";

interface UseComposeRecipientsStateProps {
  initialRecipients?: EmailRecipient[];
  /** Addresses to pre-seed as BCC (e.g. a support inbox's group address). */
  initialBccRecipients?: EmailRecipient[];
}

/**
 * Hook for managing compose recipients state
 * Handles To, CC, BCC recipients and mode switching
 */
export function useComposeRecipientsState({
  initialRecipients,
  initialBccRecipients,
}: UseComposeRecipientsStateProps = {}) {
  const [recipients, setRecipients] = useState<EmailRecipient[]>(initialRecipients || []);
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<EmailRecipient[]>(initialBccRecipients || []);
  const [ccBccMode, setCcBccMode] = useState<"cc" | "bcc">("cc");

  const handleRecipientsChange = useCallback((newRecipients: EmailRecipient[]) => {
    setRecipients(newRecipients);
  }, []);

  const handleSetCcBccMode = useCallback((mode: "cc" | "bcc") => {
    setCcBccMode(mode);
  }, []);

  const handleSetCcRecipients = useCallback((newCcRecipients: EmailRecipient[]) => {
    setCcRecipients(newCcRecipients);
  }, []);

  const handleSetBccRecipients = useCallback((newBccRecipients: EmailRecipient[]) => {
    setBccRecipients(newBccRecipients);
  }, []);

  return useMemo(
    () => ({
      recipients,
      ccRecipients,
      bccRecipients,
      ccBccMode,
      setRecipients,
      setCcRecipients,
      setBccRecipients,
      setCcBccMode,
      handleRecipientsChange,
      handleSetCcBccMode,
      handleSetCcRecipients,
      handleSetBccRecipients,
    }),
    [
      recipients,
      ccRecipients,
      bccRecipients,
      ccBccMode,
      handleRecipientsChange,
      handleSetCcBccMode,
      handleSetCcRecipients,
      handleSetBccRecipients,
    ]
  );
}
